import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Upload, Save, Plus, Trash2, ArrowRight, User } from "lucide-react";

const EMPTY = {
  name: "", title: "", credentials: "", location: "", since_year: "",
  bio_paragraphs: [""], tagline: "", footer: "", photo_url: "",
  gallery_urls: [],
};

export default function AdminInstructorBio() {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [galUploading, setGalUploading] = useState(false);
  const fileRef = useRef();
  const galRef = useRef();

  useEffect(() => {
    api.get("/instructor-bio").then((r) => setForm({
      ...EMPTY,
      ...r.data,
      bio_paragraphs: (r.data?.bio_paragraphs?.length ? r.data.bio_paragraphs : [""]),
    })).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setPara = (i) => (e) => setForm((f) => {
    const next = [...f.bio_paragraphs];
    next[i] = e.target.value;
    return { ...f, bio_paragraphs: next };
  });
  const addPara = () => setForm((f) => ({ ...f, bio_paragraphs: [...f.bio_paragraphs, ""] }));
  const rmPara = (i) => setForm((f) => ({
    ...f,
    bio_paragraphs: f.bio_paragraphs.filter((_, idx) => idx !== i).length
      ? f.bio_paragraphs.filter((_, idx) => idx !== i)
      : [""],
  }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/instructor-bio", form);
      toast.success("Saved — the /meet page has been updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const onPickFile = () => fileRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo must be under 5 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/instructor-bio/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, photo_url: r.data.photo_url }));
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onPickGalleryFile = () => galRef.current?.click();
  const onGalleryFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo must be under 5 MB.");
      return;
    }
    setGalUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/instructor-bio/gallery", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, gallery_urls: r.data.gallery_urls }));
      toast.success("Photo added to gallery.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setGalUploading(false);
      e.target.value = "";
    }
  };

  const removeGalleryPhoto = (i) => {
    setForm((f) => ({ ...f, gallery_urls: f.gallery_urls.filter((_, idx) => idx !== i) }));
    toast.info("Removed — click Save changes to make it permanent.");
  };

  const previewSrc = form.photo_url
    ? (form.photo_url.startsWith("http") ? form.photo_url : `${BACKEND_URL}${form.photo_url}`)
    : null;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 lg:p-10" data-testid="admin-instructor-bio">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="label-caps mb-2">Meet your instructor</div>
            <h1 className="font-serif text-4xl md:text-5xl leading-none">Your public bio</h1>
            <p className="text-muted-foreground mt-2">
              Everything on this page shows up at <Link to="/meet" className="underline text-primary">/meet</Link> — your public "Meet your tax professional" page.
            </p>
          </div>
          <Button asChild variant="outline"><Link to="/meet" data-testid="admin-bio-preview">Preview page <ArrowRight size={14} className="ml-1.5" /></Link></Button>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Photo uploader */}
          <Card className="p-6 md:col-span-1 h-fit">
            <div className="label-caps text-[10px] mb-3">Photo</div>
            <div className="aspect-[3/4] rounded-md overflow-hidden bg-secondary/30 border border-border mb-3 grid place-items-center">
              {previewSrc ? (
                <img src={previewSrc} alt="" className="w-full h-full object-cover" data-testid="admin-bio-photo-preview" />
              ) : (
                <div className="text-center text-muted-foreground p-6">
                  <User size={32} className="mx-auto mb-2 opacity-40" />
                  <div className="text-xs">No photo yet</div>
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="admin-bio-photo-input" />
            <Button
              onClick={onPickFile}
              disabled={uploading}
              className="w-full"
              variant={previewSrc ? "outline" : "default"}
              data-testid="admin-bio-photo-btn"
            >
              <Upload size={14} className="mr-2" />
              {uploading ? "Uploading…" : previewSrc ? "Replace photo" : "Upload photo"}
            </Button>
            <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
              Portrait works best (3:4 ratio). JPG, PNG, or WebP, up to 5 MB. Try a well-lit headshot.
            </p>
          </Card>

          {/* Text fields */}
          <Card className="p-6 md:col-span-2 space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Full name" v={form.name} onChange={set("name")} testid="admin-bio-name" />
              <Field label="Title / role" v={form.title} onChange={set("title")} testid="admin-bio-title"
                     placeholder="Owner & Operator | DyNamic Tax" />
            </div>
            <Field
              label="Credentials"
              v={form.credentials}
              onChange={set("credentials")}
              testid="admin-bio-credentials"
              placeholder="IRS Authorized ERO & Certified Acceptance Agent (CAA)"
            />
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Location" v={form.location} onChange={set("location")} testid="admin-bio-location"
                     placeholder="Winston-Salem, North Carolina" />
              <Field label="Serving clients since" v={form.since_year} onChange={set("since_year")}
                     testid="admin-bio-since" placeholder="2018" />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="label-caps text-[10px]">Bio paragraphs</Label>
                <Button size="sm" variant="ghost" onClick={addPara} data-testid="admin-bio-add-para">
                  <Plus size={12} className="mr-1" /> Add paragraph
                </Button>
              </div>
              <div className="space-y-3">
                {form.bio_paragraphs.map((p, i) => (
                  <div key={i} className="flex gap-2 items-start">
                    <Textarea
                      value={p}
                      onChange={setPara(i)}
                      rows={4}
                      placeholder={`Paragraph ${i + 1}…`}
                      data-testid={`admin-bio-para-${i}`}
                      className="flex-1"
                    />
                    <Button
                      variant="ghost" size="sm" onClick={() => rmPara(i)}
                      className="mt-1" aria-label="Remove paragraph"
                      data-testid={`admin-bio-para-remove-${i}`}
                    >
                      <Trash2 size={13} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <Field
              label="Tagline (large text at the bottom of the bio)"
              v={form.tagline} onChange={set("tagline")}
              testid="admin-bio-tagline"
              placeholder="Professional. Knowledgeable. Client-Focused."
            />
            <Field
              label="Footer strip (colored band at the very bottom of the page)"
              v={form.footer} onChange={set("footer")}
              testid="admin-bio-footer"
              placeholder="Trusted Tax & Business Support You Can Build With."
            />

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="label-caps text-[10px]">Office / workspace photos ({form.gallery_urls.length}/6)</Label>
                <input ref={galRef} type="file" accept="image/*" hidden onChange={onGalleryFile} data-testid="admin-bio-gallery-input" />
                <Button
                  size="sm" variant="ghost" onClick={onPickGalleryFile}
                  disabled={galUploading || form.gallery_urls.length >= 6}
                  data-testid="admin-bio-gallery-add"
                >
                  <Plus size={12} className="mr-1" /> {galUploading ? "Uploading…" : "Add photo"}
                </Button>
              </div>
              {form.gallery_urls.length === 0 ? (
                <div className="text-xs text-muted-foreground border border-dashed border-border rounded-md p-4">
                  No secondary photos yet. Add 2-3 shots of your office, desk, or a group photo — they'll appear in the "Around the office" section on /meet.
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {form.gallery_urls.map((url, i) => {
                    const src = url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
                    return (
                      <div key={i} className="relative aspect-[4/5] rounded-md overflow-hidden bg-secondary/30 border border-border group">
                        <img src={src} alt="" className="w-full h-full object-cover" />
                        <button
                          onClick={() => removeGalleryPhoto(i)}
                          className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/60 hover:bg-black/80 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label="Remove photo"
                          data-testid={`admin-bio-gallery-remove-${i}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="pt-2 flex justify-end">
              <Button onClick={save} disabled={saving} data-testid="admin-bio-save">
                <Save size={14} className="mr-2" /> {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
}

const Field = ({ label, v, onChange, placeholder, testid }) => (
  <div>
    <Label className="label-caps text-[10px]">{label}</Label>
    <Input value={v || ""} onChange={onChange} placeholder={placeholder} data-testid={testid} className="mt-1.5" />
  </div>
);
